import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import { DEFAULT_LOCALE } from './config/seo';

export type Locale = 'zh' | 'en' | 'ja' | 'ko' | 'de' | 'fr' | 'pt';

const LOCALE_KEY = 'lumaframe.locale.v1';

export const localeOptions: { value: Locale; label: string; htmlLang: string }[] = [
  { value: 'zh', label: '中文', htmlLang: 'zh-CN' },
  { value: 'en', label: 'English', htmlLang: 'en' },
  { value: 'ja', label: '日本語', htmlLang: 'ja' },
  { value: 'ko', label: '한국어', htmlLang: 'ko' },
  { value: 'de', label: 'Deutsch', htmlLang: 'de' },
  { value: 'fr', label: 'Français', htmlLang: 'fr' },
  { value: 'pt', label: 'Português', htmlLang: 'pt' }
];

export const dictionaries: Record<Locale, Record<string, string>> = {
  en: {
    'meta.title': 'LumaFrame Photo',
    'meta.description': 'LumaFrame is a free online photo frame editor with borders, shadows, EXIF captions, presets, and private in-browser export.',
    'meta.keywords': 'photo frame editor, online photo border, add border to photo, EXIF photo frame, local photo editor, free photo frame',
    'nav.support': 'Support',
    'nav.about': 'About',
    'nav.export': 'Export',
    'nav.language': 'Language',
    'nav.light': 'Switch to light mode',
    'nav.dark': 'Switch to dark mode',
    'nav.menu': 'Open navigation menu',
    'home.dropTitle': 'Drop/Paste a photo here',
    'home.localBlurb': 'JPG, PNG, WebP, HEIC and HEIF are processed locally in this browser. Nothing is uploaded.',
    'home.select': 'Select one from your device',
    'home.localBadge': '100% local processing',
    'gallery.title': 'Free presets included',
    'gallery.subtitle': 'Borders, shadows, aspect ratios and metadata templates are available without a paywall.',
    'gallery.white': 'White border',
    'gallery.black': 'Black frame',
    'gallery.shadow': 'Soft shadow',
    'gallery.print': 'Print matte',
    'gallery.exif': 'EXIF signature',
    'gallery.cinema': 'Cinema 16:9',
    'gallery.square': 'Square social',
    'gallery.minimalBlack': 'Minimal black',
    'gallery.rounded': 'Rounded card',
    'gallery.dark': 'Gallery dark',
    'gallery.large': 'Large border',
    'gallery.clean': 'Clean white',
    'landing.valueTitle': 'A private online photo frame editor for finished images',
    'landing.valueBody': 'LumaFrame adds clean borders, soft shadows, rounded corners, aspect-ratio canvases, and EXIF captions without sending your photo anywhere. It is built for photographers and creators who need a fast finishing step before posting, printing, or sharing.',
    'landing.privacyTitle': 'Images never leave your browser',
    'landing.privacyBody': 'Your file is decoded locally, metadata is read locally, and the final JPG or PNG is rendered with Canvas on your device. There is no upload endpoint in the main workflow.',
    'landing.freeTitle': 'No ads, no watermark, no Pro gate',
    'landing.freeBody': 'Metadata captions, custom text, saved presets, transparent PNG export, quality control, and batch export are all available for free.',
    'landing.workflowTitle': 'Three-step workflow',
    'landing.workflow1Title': 'Upload',
    'landing.workflow1Body': 'Drop, paste, or choose a local photo.',
    'landing.workflow2Title': 'Frame',
    'landing.workflow2Body': 'Tune border width, ratio, shadow, corners, and EXIF text.',
    'landing.workflow3Title': 'Export',
    'landing.workflow3Body': 'Download JPG or PNG at original, preset, or custom size.',
    'landing.formatsTitle': 'Supported formats',
    'landing.formatsBody': 'Works with JPG, PNG, WebP, HEIC, and HEIF. HEIC conversion happens locally and may take longer for very large phone images.',
    'landing.faqTitle': 'FAQ',
    'landing.faq1Q': 'Is LumaFrame free?',
    'landing.faq1A': 'Yes. The editor is free, has no ads, and does not add watermarks.',
    'landing.faq2Q': 'Are my photos uploaded?',
    'landing.faq2A': 'No. Photo processing runs in your browser with Canvas, and your images are not uploaded by the app.',
    'landing.faq3Q': 'Can I show camera metadata?',
    'landing.faq3A': 'Yes. LumaFrame can read EXIF metadata such as camera, lens, ISO, aperture, shutter speed, focal length, date, and optional GPS.',
    'landing.faq4Q': 'Can I export transparent PNG files?',
    'landing.faq4A': 'Yes. Choose PNG and use a transparent frame preset or lower border opacity.',
    'editor.noPhotoTitle': 'No photo loaded',
    'editor.noPhotoBody': 'Upload a local image first, then the editor will keep all processing inside your browser.',
    'editor.backUpload': 'Back to upload',
    'editor.newPhoto': 'New photo',
    'editor.addBatch': 'Add batch',
    'preview.label': 'Preview of {file}',
    'preset.local': 'Local preset',
    'preset.placeholder': 'Preset name',
    'preset.save': 'Save current preset',
    'preset.export': 'Export',
    'preset.import': 'Import',
    'preset.minimal-white.name': 'Minimal White',
    'preset.minimal-white.description': 'Clean gallery border',
    'preset.minimal-black.name': 'Minimal Black',
    'preset.minimal-black.description': 'High contrast edge',
    'preset.shadow-white.name': 'Soft Shadow',
    'preset.shadow-white.description': 'White frame with depth',
    'preset.photographer-exif.name': 'Photographer',
    'preset.photographer-exif.description': 'Metadata signature',
    'preset.instagram-square.name': 'Instagram',
    'preset.instagram-square.description': 'Square social crop',
    'preset.print-large.name': 'Print',
    'preset.print-large.description': 'Large lower margin',
    'preset.cinema-169.name': 'Cinema 16:9',
    'preset.cinema-169.description': 'Wide matte frame',
    'preset.story-916.name': 'Story',
    'preset.story-916.description': 'Vertical social frame',
    'preset.rounded-card.name': 'Rounded',
    'preset.rounded-card.description': 'Soft corners',
    'preset.transparent-png.name': 'Transparent',
    'preset.transparent-png.description': 'PNG-friendly edge',
    'inspector.noPhoto': 'No photo',
    'inspector.uploadBegin': 'Upload to begin',
    'inspector.undo': 'Undo',
    'inspector.redo': 'Redo',
    'inspector.frame': 'Frame',
    'inspector.borderUnit': 'Border unit',
    'inspector.percent': 'Percent',
    'inspector.pixels': 'Pixels',
    'inspector.borderWidth': 'Border width',
    'inspector.borderColor': 'Border color',
    'inspector.opacity': 'Opacity',
    'inspector.aspectRatio': 'Aspect ratio',
    'inspector.original': 'Original',
    'inspector.custom': 'Custom',
    'inspector.width': 'Width',
    'inspector.height': 'Height',
    'inspector.shadow': 'Shadow',
    'inspector.enableShadow': 'Enable shadow',
    'inspector.none': 'None',
    'inspector.light': 'Light',
    'inspector.medium': 'Medium',
    'inspector.strong': 'Strong',
    'inspector.strength': 'Strength',
    'inspector.blur': 'Blur',
    'inspector.offsetX': 'Offset X',
    'inspector.offsetY': 'Offset Y',
    'inspector.shadowColor': 'Shadow color',
    'inspector.corners': 'Corners',
    'inspector.roundedCorners': 'Rounded corners',
    'inspector.linkCorners': 'Link all corners',
    'inspector.radius': 'Radius',
    'inspector.topL': 'Top L',
    'inspector.topR': 'Top R',
    'inspector.bottomR': 'Bottom R',
    'inspector.bottomL': 'Bottom L',
    'inspector.metadata': 'Metadata',
    'inspector.showExif': 'Show EXIF and text',
    'inspector.position': 'Position',
    'inspector.customText': 'Custom text',
    'inspector.customTextPlaceholder': '@yourname or signature',
    'inspector.fontSize': 'Font size',
    'inspector.textColor': 'Text color',
    'inspector.camera': 'Camera',
    'inspector.lens': 'Lens',
    'inspector.exposure': 'Exposure',
    'inspector.date': 'Date',
    'inspector.gps': 'GPS',
    'inspector.exportSection': 'Export',
    'inspector.format': 'Format',
    'inspector.quality': 'Quality',
    'inspector.size': 'Size',
    'inspector.preset': 'Preset',
    'inspector.longEdge': 'Long edge',
    'inspector.fileName': 'File name',
    'inspector.filePlaceholder': 'original_framed',
    'inspector.download': 'Download',
    'inspector.copy': 'Copy',
    'inspector.batchExport': 'Batch export',
    'about.title': 'Private photo framing, without the toll booth.',
    'about.p1': 'LumaFrame is a pure frontend tool. Photo decoding, EXIF parsing, preview rendering, and final export happen in your browser with Canvas. The app does not upload original images, generated images, presets, or metadata to a server.',
    'about.p2': 'Every feature is free: EXIF text, custom signatures, border control, shadows, rounded corners, presets, batch export, PNG transparency, quality settings, and PWA offline use.',
    'about.p3': 'Cloudflare Pages hosts the static assets only. There is no backend in the main workflow, which keeps the privacy model simple and inspectable.',
    'about.card1Title': 'Local-first',
    'about.card1Body': 'Files stay on the device.',
    'about.card2Title': 'No ads',
    'about.card2Body': 'No tracking-driven interface.',
    'about.card3Title': 'Free presets',
    'about.card3Body': 'Former Pro-style controls are open.',
    'support.title': 'Support',
    'support.uploadTitle': 'Upload',
    'support.uploadBody': 'Drop a photo anywhere on the upload page, paste an image with Ctrl+V, or choose a local file. Multiple files enable batch export.',
    'support.editTitle': 'Edit',
    'support.editBody': 'Use presets for a starting point, then tune border width, color, aspect ratio, shadow, corners, and EXIF text. Changes are rendered live.',
    'support.exportTitle': 'Export',
    'support.exportBody': 'Choose JPG or PNG, quality, size, and file name. Ctrl+S exports the active photo. Batch export downloads every loaded image with the same settings.',
    'support.keyboardTitle': 'Keyboard',
    'support.keyboardBody': 'Ctrl+Z undo, Ctrl+Y redo, Ctrl+S export, Esc returns to the upload page. On macOS, Command works too.',
    'support.heicTitle': 'HEIC notes',
    'support.heicBody': 'HEIC support depends on browser decoding and the bundled converter. Very large phone photos may take longer because conversion happens locally.',
    'notice.appliedPreset': 'Applied {name}.',
    'notice.savedPreset': 'Saved {name} as a local preset.',
    'notice.importedPresets': 'Imported {count} preset(s).',
    'notice.exportReady': 'Export ready.',
    'notice.copied': 'Copied PNG to clipboard.',
    'notice.batchExported': 'Exported {count} image(s).',
    'error.chooseImage': 'Please choose an image file.',
    'error.unsupported': 'Unsupported format. Please use JPG, PNG, WebP, HEIC, or HEIF.',
    'error.tooLarge': 'This file is larger than 50 MB. Please choose a smaller photo.',
    'error.decode': 'Could not decode this image in the browser.',
    'error.loadPhoto': 'Could not load this photo.',
    'error.presetArray': 'Preset file must be an array.',
    'error.importPresets': 'Could not import presets.',
    'error.exportImage': 'Could not export this image.',
    'error.copyUnsupported': 'Clipboard image copy is not supported in this browser.',
    'error.copyImage': 'Could not copy this image.',
    'error.batchExport': 'Could not batch export these images.'
  },
  zh: {
    'meta.title': 'LumaFrame 照片加框',
    'meta.description': 'LumaFrame 是免费的在线照片加框工具，支持边框、阴影、EXIF 字幕、预设和浏览器本地导出。',
    'meta.keywords': '照片加框,在线照片边框,图片加边框,EXIF 照片框,本地图片编辑,免费照片加框',
    'nav.support': '支持',
    'nav.about': '关于',
    'nav.export': '导出',
    'nav.language': '语言',
    'nav.light': '切换到浅色模式',
    'nav.dark': '切换到深色模式',
    'nav.menu': '打开导航菜单',
    'home.dropTitle': '拖放/粘贴照片到这里',
    'home.localBlurb': '支持 JPG、PNG、WebP、HEIC 和 HEIF，全部在本浏览器本地处理，不上传。',
    'home.select': '从设备选择一张',
    'home.localBadge': '100% 本地处理',
    'gallery.title': '内置免费预设',
    'gallery.subtitle': '边框、阴影、比例和元数据模板全部免费开放。',
    'gallery.white': '白色边框',
    'gallery.black': '黑色边框',
    'gallery.shadow': '柔和阴影',
    'gallery.print': '打印留白',
    'gallery.exif': 'EXIF 签名',
    'gallery.cinema': '电影 16:9',
    'gallery.square': '社交方图',
    'gallery.minimalBlack': '极简黑框',
    'gallery.rounded': '圆角卡片',
    'gallery.dark': '暗色画廊',
    'gallery.large': '大边框',
    'gallery.clean': '干净白框',
    'landing.valueTitle': '面向成片发布的私密在线照片加框工具',
    'landing.valueBody': 'LumaFrame 可以添加干净边框、柔和阴影、圆角、比例画布和 EXIF 字幕。照片不会上传，适合摄影师和创作者在发布、打印或分享前快速完成最后一步。',
    'landing.privacyTitle': '图片永不离开浏览器',
    'landing.privacyBody': '文件在本地解码，元数据在本地读取，最终 JPG 或 PNG 通过 Canvas 在设备上生成。主流程没有上传接口。',
    'landing.freeTitle': '无广告、无水印、无 Pro 门槛',
    'landing.freeBody': '元数据字幕、自定义文本、保存预设、透明 PNG、质量控制和批量导出全部免费开放。',
    'landing.workflowTitle': '三步完成',
    'landing.workflow1Title': '上传',
    'landing.workflow1Body': '拖放、粘贴或选择本地照片。',
    'landing.workflow2Title': '加框',
    'landing.workflow2Body': '调整边框宽度、比例、阴影、圆角和 EXIF 文本。',
    'landing.workflow3Title': '导出',
    'landing.workflow3Body': '按原始、预设或自定义尺寸下载 JPG 或 PNG。',
    'landing.formatsTitle': '支持格式',
    'landing.formatsBody': '支持 JPG、PNG、WebP、HEIC 和 HEIF。HEIC 转换在本地完成，大尺寸手机照片可能需要更久。',
    'landing.faqTitle': '常见问题',
    'landing.faq1Q': 'LumaFrame 是免费的吗？',
    'landing.faq1A': '是的。编辑器免费、无广告，也不会添加水印。',
    'landing.faq2Q': '我的照片会上传吗？',
    'landing.faq2A': '不会。照片处理通过 Canvas 在浏览器中运行，应用不会上传你的图片。',
    'landing.faq3Q': '可以显示相机元数据吗？',
    'landing.faq3A': '可以。LumaFrame 可以读取相机、镜头、ISO、光圈、快门、焦距、日期和可选 GPS 等 EXIF 信息。',
    'landing.faq4Q': '可以导出透明 PNG 吗？',
    'landing.faq4A': '可以。选择 PNG，并使用透明预设或降低边框不透明度。',
    'editor.noPhotoTitle': '未载入照片',
    'editor.noPhotoBody': '先上传一张本地图片，编辑器会把所有处理都保留在你的浏览器内。',
    'editor.backUpload': '返回上传',
    'editor.newPhoto': '新照片',
    'editor.addBatch': '添加批量',
    'preview.label': '{file} 的预览',
    'preset.local': '本地预设',
    'preset.placeholder': '预设名称',
    'preset.save': '保存当前预设',
    'preset.export': '导出',
    'preset.import': '导入',
    'preset.minimal-white.name': '极简白框',
    'preset.minimal-white.description': '干净的画廊边框',
    'preset.minimal-black.name': '极简黑框',
    'preset.minimal-black.description': '高对比边缘',
    'preset.shadow-white.name': '柔和阴影',
    'preset.shadow-white.description': '有层次的白框',
    'preset.photographer-exif.name': '摄影师风格',
    'preset.photographer-exif.description': '元数据签名',
    'preset.instagram-square.name': 'Instagram',
    'preset.instagram-square.description': '社交方图裁切',
    'preset.print-large.name': '打印风格',
    'preset.print-large.description': '更大的底部留白',
    'preset.cinema-169.name': '电影 16:9',
    'preset.cinema-169.description': '宽幅哑光框',
    'preset.story-916.name': '故事竖图',
    'preset.story-916.description': '竖向社交画框',
    'preset.rounded-card.name': '圆角',
    'preset.rounded-card.description': '柔和圆角',
    'preset.transparent-png.name': '透明',
    'preset.transparent-png.description': '适合 PNG 的边缘',
    'inspector.noPhoto': '无照片',
    'inspector.uploadBegin': '上传后开始',
    'inspector.undo': '撤销',
    'inspector.redo': '重做',
    'inspector.frame': '边框',
    'inspector.borderUnit': '边框单位',
    'inspector.percent': '百分比',
    'inspector.pixels': '像素',
    'inspector.borderWidth': '边框宽度',
    'inspector.borderColor': '边框颜色',
    'inspector.opacity': '不透明度',
    'inspector.aspectRatio': '画布比例',
    'inspector.original': '原比例',
    'inspector.custom': '自定义',
    'inspector.width': '宽度',
    'inspector.height': '高度',
    'inspector.shadow': '阴影',
    'inspector.enableShadow': '启用阴影',
    'inspector.none': '无',
    'inspector.light': '轻微',
    'inspector.medium': '中等',
    'inspector.strong': '强烈',
    'inspector.strength': '强度',
    'inspector.blur': '模糊',
    'inspector.offsetX': 'X 偏移',
    'inspector.offsetY': 'Y 偏移',
    'inspector.shadowColor': '阴影颜色',
    'inspector.corners': '圆角',
    'inspector.roundedCorners': '启用圆角',
    'inspector.linkCorners': '联动四角',
    'inspector.radius': '半径',
    'inspector.topL': '左上',
    'inspector.topR': '右上',
    'inspector.bottomR': '右下',
    'inspector.bottomL': '左下',
    'inspector.metadata': '元数据',
    'inspector.showExif': '显示 EXIF 和文本',
    'inspector.position': '位置',
    'inspector.customText': '自定义文本',
    'inspector.customTextPlaceholder': '@你的名字 或签名',
    'inspector.fontSize': '字号',
    'inspector.textColor': '文字颜色',
    'inspector.camera': '相机',
    'inspector.lens': '镜头',
    'inspector.exposure': '曝光',
    'inspector.date': '日期',
    'inspector.gps': 'GPS',
    'inspector.exportSection': '导出',
    'inspector.format': '格式',
    'inspector.quality': '质量',
    'inspector.size': '尺寸',
    'inspector.preset': '预设',
    'inspector.longEdge': '长边',
    'inspector.fileName': '文件名',
    'inspector.filePlaceholder': 'original_framed',
    'inspector.download': '下载',
    'inspector.copy': '复制',
    'inspector.batchExport': '批量导出',
    'about.title': '私密照片加框，不设门槛。',
    'about.p1': 'LumaFrame 是纯前端工具。照片解码、EXIF 解析、预览渲染和最终导出都通过 Canvas 在浏览器中完成。应用不会上传原图、生成图、预设或元数据。',
    'about.p2': '所有功能都免费：EXIF 文本、自定义签名、边框控制、阴影、圆角、预设、批量导出、PNG 透明、质量设置和 PWA 离线使用。',
    'about.p3': 'Cloudflare Pages 只托管静态资源。主流程没有后端，让隐私模型保持简单且可检查。',
    'about.card1Title': '本地优先',
    'about.card1Body': '文件保留在设备上。',
    'about.card2Title': '无广告',
    'about.card2Body': '没有由追踪驱动的界面。',
    'about.card3Title': '免费预设',
    'about.card3Body': '原 Pro 风格控制全部开放。',
    'support.title': '支持',
    'support.uploadTitle': '上传',
    'support.uploadBody': '在上传页任意位置拖放照片，用 Ctrl+V 粘贴图片，或选择本地文件。多选文件会启用批量导出。',
    'support.editTitle': '编辑',
    'support.editBody': '先用预设作为起点，再调整边框宽度、颜色、比例、阴影、圆角和 EXIF 文本。修改会实时渲染。',
    'support.exportTitle': '导出',
    'support.exportBody': '选择 JPG 或 PNG、质量、尺寸和文件名。Ctrl+S 导出当前照片，批量导出会用同一套设置下载所有已载入图片。',
    'support.keyboardTitle': '键盘',
    'support.keyboardBody': 'Ctrl+Z 撤销，Ctrl+Y 重做，Ctrl+S 导出，Esc 返回上传页。macOS 上 Command 也可使用。',
    'support.heicTitle': 'HEIC 说明',
    'support.heicBody': 'HEIC 支持取决于浏览器解码和内置转换器。大尺寸手机照片可能需要更久，因为转换在本地完成。',
    'notice.appliedPreset': '已应用 {name}。',
    'notice.savedPreset': '已将 {name} 保存为本地预设。',
    'notice.importedPresets': '已导入 {count} 个预设。',
    'notice.exportReady': '导出已准备好。',
    'notice.copied': '已复制 PNG 到剪贴板。',
    'notice.batchExported': '已导出 {count} 张图片。',
    'error.chooseImage': '请选择图片文件。',
    'error.unsupported': '格式不支持。请使用 JPG、PNG、WebP、HEIC 或 HEIF。',
    'error.tooLarge': '文件超过 50 MB，请选择更小的照片。',
    'error.decode': '浏览器无法解码这张图片。',
    'error.loadPhoto': '无法载入这张照片。',
    'error.presetArray': '预设文件必须是数组。',
    'error.importPresets': '无法导入预设。',
    'error.exportImage': '无法导出这张图片。',
    'error.copyUnsupported': '此浏览器不支持复制图片到剪贴板。',
    'error.copyImage': '无法复制这张图片。',
    'error.batchExport': '无法批量导出这些图片。'
  },
  ja: {
    'meta.title': 'LumaFrame 写真フレーム',
    'meta.description': 'LumaFrame は無料のオンライン写真フレーム編集ツールです。枠、影、EXIF キャプション、プリセット、ブラウザ内書き出しに対応します。',
    'meta.keywords': '写真フレーム,オンライン写真枠,写真に枠を追加,EXIF フレーム,ローカル写真編集,免费写真フレーム',
    'nav.support': 'サポート',
    'nav.about': '概要',
    'nav.export': '書き出し',
    'nav.language': '言語',
    'nav.light': 'ライトモードへ',
    'nav.dark': 'ダークモードへ',
    'nav.menu': 'ナビゲーションを開く',
    'home.dropTitle': 'ここに写真をドロップ/貼り付け',
    'home.localBlurb': 'JPG、PNG、WebP、HEIC、HEIF をこのブラウザ内でローカル処理します。アップロードはしません。',
    'home.select': 'デバイスから選択',
    'home.localBadge': '100% ローカル処理',
    'gallery.title': '無料プリセット付き',
    'gallery.subtitle': '枠、影、比率、メタデータテンプレートを無料で使えます。',
    'gallery.white': '白フレーム',
    'gallery.black': '黒フレーム',
    'gallery.shadow': '柔らかい影',
    'gallery.print': 'プリント余白',
    'gallery.exif': 'EXIF サイン',
    'gallery.cinema': 'シネマ 16:9',
    'gallery.square': '正方形 SNS',
    'gallery.minimalBlack': 'ミニマル黒',
    'gallery.rounded': '角丸カード',
    'gallery.dark': 'ダークギャラリー',
    'gallery.large': '大きな余白',
    'gallery.clean': 'クリーン白',
    'landing.valueTitle': '仕上げ画像のためのプライベートなオンライン写真フレーム',
    'landing.valueBody': 'LumaFrame は清潔な枠、柔らかい影、角丸、比率キャンバス、EXIF キャプションを追加できます。写真はアップロードされず、投稿、印刷、共有前の仕上げに向いています。',
    'landing.privacyTitle': '画像はブラウザから出ません',
    'landing.privacyBody': 'ファイルのデコード、メタデータ読み取り、JPG/PNG の生成はすべて Canvas により端末上で行われます。メイン処理にアップロード先はありません。',
    'landing.freeTitle': '広告なし、透かしなし、Pro 制限なし',
    'landing.freeBody': 'メタデータキャプション、カスタムテキスト、保存プリセット、透明 PNG、品質設定、一括書き出しを無料で使えます。',
    'landing.workflowTitle': '3ステップ',
    'landing.workflow1Title': 'アップロード',
    'landing.workflow1Body': '写真をドロップ、貼り付け、または選択します。',
    'landing.workflow2Title': 'フレーム',
    'landing.workflow2Body': '枠幅、比率、影、角丸、EXIF テキストを調整します。',
    'landing.workflow3Title': '書き出し',
    'landing.workflow3Body': '元サイズ、プリセット、カスタムサイズで JPG/PNG を保存します。',
    'landing.formatsTitle': '対応形式',
    'landing.formatsBody': 'JPG、PNG、WebP、HEIC、HEIF に対応します。HEIC 変換はローカルで行われ、大きなスマホ写真は時間がかかる場合があります。',
    'landing.faqTitle': 'FAQ',
    'landing.faq1Q': 'LumaFrame は無料ですか？',
    'landing.faq1A': 'はい。無料で、広告も透かしもありません。',
    'landing.faq2Q': '写真はアップロードされますか？',
    'landing.faq2A': 'いいえ。写真処理は Canvas によりブラウザ内で行われ、画像はアップロードされません。',
    'landing.faq3Q': 'カメラのメタデータを表示できますか？',
    'landing.faq3A': 'はい。カメラ、レンズ、ISO、絞り、シャッター速度、焦点距離、日付、任意の GPS などを表示できます。',
    'landing.faq4Q': '透明 PNG を書き出せますか？',
    'landing.faq4A': 'はい。PNG を選び、透明プリセットを使うか枠の不透明度を下げてください。',
    'editor.noPhotoTitle': '写真が読み込まれていません',
    'editor.noPhotoBody': 'まずローカル画像をアップロードしてください。編集処理はすべてブラウザ内で行われます。',
    'editor.backUpload': 'アップロードへ戻る',
    'editor.newPhoto': '新しい写真',
    'editor.addBatch': '一括追加',
    'preview.label': '{file} のプレビュー',
    'preset.local': 'ローカルプリセット',
    'preset.placeholder': 'プリセット名',
    'preset.save': '現在のプリセットを保存',
    'preset.export': '書き出し',
    'preset.import': '読み込み',
    'preset.minimal-white.name': 'ミニマル白',
    'preset.minimal-white.description': 'ギャラリー風の清潔な枠',
    'preset.minimal-black.name': 'ミニマル黒',
    'preset.minimal-black.description': '高コントラストの縁',
    'preset.shadow-white.name': 'ソフトシャドウ',
    'preset.shadow-white.description': '奥行きのある白枠',
    'preset.photographer-exif.name': 'フォトグラファー',
    'preset.photographer-exif.description': 'メタデータ署名',
    'preset.instagram-square.name': 'Instagram',
    'preset.instagram-square.description': '正方形の SNS クロップ',
    'preset.print-large.name': 'プリント',
    'preset.print-large.description': '下部余白を大きく',
    'preset.cinema-169.name': 'シネマ 16:9',
    'preset.cinema-169.description': 'ワイドなマット枠',
    'preset.story-916.name': 'ストーリー',
    'preset.story-916.description': '縦型 SNS フレーム',
    'preset.rounded-card.name': '角丸',
    'preset.rounded-card.description': '柔らかい角',
    'preset.transparent-png.name': '透明',
    'preset.transparent-png.description': 'PNG 向けの縁',
    'inspector.noPhoto': '写真なし',
    'inspector.uploadBegin': 'アップロードして開始',
    'inspector.undo': '元に戻す',
    'inspector.redo': 'やり直す',
    'inspector.frame': 'フレーム',
    'inspector.borderUnit': '枠の単位',
    'inspector.percent': 'パーセント',
    'inspector.pixels': 'ピクセル',
    'inspector.borderWidth': '枠の幅',
    'inspector.borderColor': '枠の色',
    'inspector.opacity': '不透明度',
    'inspector.aspectRatio': 'アスペクト比',
    'inspector.original': '元の比率',
    'inspector.custom': 'カスタム',
    'inspector.width': '幅',
    'inspector.height': '高さ',
    'inspector.shadow': '影',
    'inspector.enableShadow': '影を有効化',
    'inspector.none': 'なし',
    'inspector.light': '弱',
    'inspector.medium': '中',
    'inspector.strong': '強',
    'inspector.strength': '強さ',
    'inspector.blur': 'ぼかし',
    'inspector.offsetX': 'X オフセット',
    'inspector.offsetY': 'Y オフセット',
    'inspector.shadowColor': '影の色',
    'inspector.corners': '角丸',
    'inspector.roundedCorners': '角丸を有効化',
    'inspector.linkCorners': '四隅を連動',
    'inspector.radius': '半径',
    'inspector.topL': '左上',
    'inspector.topR': '右上',
    'inspector.bottomR': '右下',
    'inspector.bottomL': '左下',
    'inspector.metadata': 'メタデータ',
    'inspector.showExif': 'EXIF とテキストを表示',
    'inspector.position': '位置',
    'inspector.customText': 'カスタムテキスト',
    'inspector.customTextPlaceholder': '@yourname または署名',
    'inspector.fontSize': '文字サイズ',
    'inspector.textColor': '文字色',
    'inspector.camera': 'カメラ',
    'inspector.lens': 'レンズ',
    'inspector.exposure': '露出',
    'inspector.date': '日付',
    'inspector.gps': 'GPS',
    'inspector.exportSection': '書き出し',
    'inspector.format': '形式',
    'inspector.quality': '品質',
    'inspector.size': 'サイズ',
    'inspector.preset': 'プリセット',
    'inspector.longEdge': '長辺',
    'inspector.fileName': 'ファイル名',
    'inspector.filePlaceholder': 'original_framed',
    'inspector.download': 'ダウンロード',
    'inspector.copy': 'コピー',
    'inspector.batchExport': '一括書き出し',
    'about.title': '課金の壁なしで、写真をプライベートにフレーム化。',
    'about.p1': 'LumaFrame は純粋なフロントエンドツールです。写真のデコード、EXIF 解析、プレビュー、最終書き出しは Canvas によりブラウザ内で行われます。原画像、生成画像、プリセット、メタデータはサーバーへ送信されません。',
    'about.p2': 'EXIF テキスト、署名、枠、影、角丸、プリセット、一括書き出し、PNG 透明、品質設定、PWA オフライン利用まで、すべて無料です。',
    'about.p3': 'Cloudflare Pages は静的ファイルだけを配信します。メインの処理にバックエンドはありません。',
    'about.card1Title': 'ローカル優先',
    'about.card1Body': 'ファイルはデバイス上に残ります。',
    'about.card2Title': '広告なし',
    'about.card2Body': '追跡ベースの UI はありません。',
    'about.card3Title': '無料プリセット',
    'about.card3Body': 'Pro 風の機能を開放しています。',
    'support.title': 'サポート',
    'support.uploadTitle': 'アップロード',
    'support.uploadBody': 'アップロード画面へ写真をドロップ、Ctrl+V で貼り付け、またはローカルファイルを選択します。複数ファイルで一括書き出しできます。',
    'support.editTitle': '編集',
    'support.editBody': 'プリセットを起点に、枠幅、色、比率、影、角丸、EXIF テキストを調整します。変更はリアルタイムで反映されます。',
    'support.exportTitle': '書き出し',
    'support.exportBody': 'JPG/PNG、品質、サイズ、ファイル名を選べます。Ctrl+S で現在の写真を書き出します。',
    'support.keyboardTitle': 'キーボード',
    'support.keyboardBody': 'Ctrl+Z で元に戻す、Ctrl+Y でやり直し、Ctrl+S で書き出し、Esc でアップロード画面へ戻ります。macOS では Command も使えます。',
    'support.heicTitle': 'HEIC について',
    'support.heicBody': 'HEIC 対応はブラウザのデコードと内蔵変換に依存します。大きなスマホ写真はローカル変換のため時間がかかる場合があります。',
    'notice.appliedPreset': '{name} を適用しました。',
    'notice.savedPreset': '{name} をローカルプリセットとして保存しました。',
    'notice.importedPresets': '{count} 件のプリセットを読み込みました。',
    'notice.exportReady': '書き出し準備ができました。',
    'notice.copied': 'PNG をクリップボードへコピーしました。',
    'notice.batchExported': '{count} 枚を書き出しました。',
    'error.chooseImage': '画像ファイルを選択してください。',
    'error.unsupported': '未対応の形式です。JPG、PNG、WebP、HEIC、HEIF を使用してください。',
    'error.tooLarge': 'ファイルが 50 MB を超えています。より小さい写真を選択してください。',
    'error.decode': 'この画像をブラウザでデコードできませんでした。',
    'error.loadPhoto': '写真を読み込めませんでした。',
    'error.presetArray': 'プリセットファイルは配列である必要があります。',
    'error.importPresets': 'プリセットを読み込めませんでした。',
    'error.exportImage': 'この画像を書き出せませんでした。',
    'error.copyUnsupported': 'このブラウザは画像のクリップボードコピーに対応していません。',
    'error.copyImage': 'この画像をコピーできませんでした。',
    'error.batchExport': 'これらの画像を一括書き出しできませんでした。'
  },
  ko: {
    'meta.title': 'LumaFrame 사진 프레임',
    'meta.description': 'LumaFrame은 무료 온라인 사진 프레임 편집기입니다. 테두리, 그림자, EXIF 캡션, 프리셋, 브라우저 내보내기를 지원합니다.',
    'meta.keywords': '사진 프레임,온라인 사진 테두리,사진에 테두리 추가,EXIF 사진 프레임,로컬 사진 편집,무료 사진 프레임',
    'nav.support': '지원',
    'nav.about': '소개',
    'nav.export': '내보내기',
    'nav.language': '언어',
    'nav.light': '라이트 모드로 전환',
    'nav.dark': '다크 모드로 전환',
    'nav.menu': '내비게이션 열기',
    'home.dropTitle': '여기에 사진을 드롭/붙여넣기',
    'home.localBlurb': 'JPG, PNG, WebP, HEIC, HEIF를 이 브라우저에서 로컬로 처리합니다. 업로드하지 않습니다.',
    'home.select': '기기에서 선택',
    'home.localBadge': '100% 로컬 처리',
    'gallery.title': '무료 프리셋 포함',
    'gallery.subtitle': '테두리, 그림자, 비율, 메타데이터 템플릿을 모두 무료로 사용할 수 있습니다.',
    'gallery.white': '화이트 테두리',
    'gallery.black': '블랙 프레임',
    'gallery.shadow': '부드러운 그림자',
    'gallery.print': '프린트 여백',
    'gallery.exif': 'EXIF 서명',
    'gallery.cinema': '시네마 16:9',
    'gallery.square': '정사각 소셜',
    'gallery.minimalBlack': '미니멀 블랙',
    'gallery.rounded': '둥근 카드',
    'gallery.dark': '다크 갤러리',
    'gallery.large': '큰 테두리',
    'gallery.clean': '깔끔한 화이트',
    'landing.valueTitle': '완성 사진을 위한 비공개 온라인 사진 프레임 편집기',
    'landing.valueBody': 'LumaFrame은 깔끔한 테두리, 부드러운 그림자, 둥근 모서리, 비율 캔버스, EXIF 캡션을 추가합니다. 사진은 업로드되지 않으며 게시, 인쇄, 공유 전 마무리에 적합합니다.',
    'landing.privacyTitle': '이미지는 브라우저를 떠나지 않습니다',
    'landing.privacyBody': '파일 디코딩, 메타데이터 읽기, JPG/PNG 생성이 모두 Canvas로 기기에서 진행됩니다. 기본 워크플로에는 업로드 엔드포인트가 없습니다.',
    'landing.freeTitle': '광고 없음, 워터마크 없음, Pro 제한 없음',
    'landing.freeBody': '메타데이터 캡션, 사용자 텍스트, 저장 프리셋, 투명 PNG, 품질 제어, 일괄 내보내기를 모두 무료로 사용할 수 있습니다.',
    'landing.workflowTitle': '3단계 워크플로',
    'landing.workflow1Title': '업로드',
    'landing.workflow1Body': '사진을 드롭, 붙여넣기 또는 선택합니다.',
    'landing.workflow2Title': '프레임',
    'landing.workflow2Body': '테두리 너비, 비율, 그림자, 모서리, EXIF 텍스트를 조정합니다.',
    'landing.workflow3Title': '내보내기',
    'landing.workflow3Body': '원본, 프리셋, 사용자 크기로 JPG 또는 PNG를 다운로드합니다.',
    'landing.formatsTitle': '지원 형식',
    'landing.formatsBody': 'JPG, PNG, WebP, HEIC, HEIF를 지원합니다. HEIC 변환은 로컬에서 진행되며 큰 휴대폰 사진은 더 오래 걸릴 수 있습니다.',
    'landing.faqTitle': 'FAQ',
    'landing.faq1Q': 'LumaFrame은 무료인가요?',
    'landing.faq1A': '네. 무료이며 광고와 워터마크가 없습니다.',
    'landing.faq2Q': '사진이 업로드되나요?',
    'landing.faq2A': '아니요. 사진 처리는 Canvas로 브라우저 안에서 실행되며 앱은 이미지를 업로드하지 않습니다.',
    'landing.faq3Q': '카메라 메타데이터를 표시할 수 있나요?',
    'landing.faq3A': '네. 카메라, 렌즈, ISO, 조리개, 셔터 속도, 초점 거리, 날짜, 선택적 GPS 등을 읽을 수 있습니다.',
    'landing.faq4Q': '투명 PNG를 내보낼 수 있나요?',
    'landing.faq4A': '네. PNG를 선택하고 투명 프리셋을 사용하거나 테두리 불투명도를 낮추면 됩니다.',
    'editor.noPhotoTitle': '사진이 없습니다',
    'editor.noPhotoBody': '먼저 로컬 이미지를 업로드하세요. 모든 편집 처리는 브라우저 안에서 이루어집니다.',
    'editor.backUpload': '업로드로 돌아가기',
    'editor.newPhoto': '새 사진',
    'editor.addBatch': '일괄 추가',
    'preview.label': '{file} 미리보기',
    'preset.local': '로컬 프리셋',
    'preset.placeholder': '프리셋 이름',
    'preset.save': '현재 프리셋 저장',
    'preset.export': '내보내기',
    'preset.import': '가져오기',
    'preset.minimal-white.name': '미니멀 화이트',
    'preset.minimal-white.description': '깔끔한 갤러리 테두리',
    'preset.minimal-black.name': '미니멀 블랙',
    'preset.minimal-black.description': '강한 대비의 가장자리',
    'preset.shadow-white.name': '소프트 섀도',
    'preset.shadow-white.description': '깊이 있는 화이트 프레임',
    'preset.photographer-exif.name': '포토그래퍼',
    'preset.photographer-exif.description': '메타데이터 서명',
    'preset.instagram-square.name': 'Instagram',
    'preset.instagram-square.description': '정사각 소셜 크롭',
    'preset.print-large.name': '프린트',
    'preset.print-large.description': '넓은 하단 여백',
    'preset.cinema-169.name': '시네마 16:9',
    'preset.cinema-169.description': '와이드 매트 프레임',
    'preset.story-916.name': '스토리',
    'preset.story-916.description': '세로 소셜 프레임',
    'preset.rounded-card.name': '라운드',
    'preset.rounded-card.description': '부드러운 모서리',
    'preset.transparent-png.name': '투명',
    'preset.transparent-png.description': 'PNG에 적합한 가장자리',
    'inspector.noPhoto': '사진 없음',
    'inspector.uploadBegin': '업로드 후 시작',
    'inspector.undo': '실행 취소',
    'inspector.redo': '다시 실행',
    'inspector.frame': '프레임',
    'inspector.borderUnit': '테두리 단위',
    'inspector.percent': '퍼센트',
    'inspector.pixels': '픽셀',
    'inspector.borderWidth': '테두리 너비',
    'inspector.borderColor': '테두리 색상',
    'inspector.opacity': '불투명도',
    'inspector.aspectRatio': '화면 비율',
    'inspector.original': '원본',
    'inspector.custom': '사용자 지정',
    'inspector.width': '너비',
    'inspector.height': '높이',
    'inspector.shadow': '그림자',
    'inspector.enableShadow': '그림자 켜기',
    'inspector.none': '없음',
    'inspector.light': '약하게',
    'inspector.medium': '보통',
    'inspector.strong': '강하게',
    'inspector.strength': '강도',
    'inspector.blur': '블러',
    'inspector.offsetX': 'X 오프셋',
    'inspector.offsetY': 'Y 오프셋',
    'inspector.shadowColor': '그림자 색상',
    'inspector.corners': '모서리',
    'inspector.roundedCorners': '둥근 모서리',
    'inspector.linkCorners': '네 모서리 연결',
    'inspector.radius': '반경',
    'inspector.topL': '왼쪽 위',
    'inspector.topR': '오른쪽 위',
    'inspector.bottomR': '오른쪽 아래',
    'inspector.bottomL': '왼쪽 아래',
    'inspector.metadata': '메타데이터',
    'inspector.showExif': 'EXIF 및 텍스트 표시',
    'inspector.position': '위치',
    'inspector.customText': '사용자 텍스트',
    'inspector.customTextPlaceholder': '@yourname 또는 서명',
    'inspector.fontSize': '글자 크기',
    'inspector.textColor': '글자 색상',
    'inspector.camera': '카메라',
    'inspector.lens': '렌즈',
    'inspector.exposure': '노출',
    'inspector.date': '날짜',
    'inspector.gps': 'GPS',
    'inspector.exportSection': '내보내기',
    'inspector.format': '형식',
    'inspector.quality': '품질',
    'inspector.size': '크기',
    'inspector.preset': '프리셋',
    'inspector.longEdge': '긴 변',
    'inspector.fileName': '파일 이름',
    'inspector.filePlaceholder': 'original_framed',
    'inspector.download': '다운로드',
    'inspector.copy': '복사',
    'inspector.batchExport': '일괄 내보내기',
    'about.title': '유료 장벽 없는 비공개 사진 프레임.',
    'about.p1': 'LumaFrame은 순수 프런트엔드 도구입니다. 사진 디코딩, EXIF 파싱, 미리보기 렌더링, 최종 내보내기가 Canvas로 브라우저 안에서 진행됩니다. 원본 이미지, 생성 이미지, 프리셋, 메타데이터를 서버로 업로드하지 않습니다.',
    'about.p2': 'EXIF 텍스트, 사용자 서명, 테두리, 그림자, 둥근 모서리, 프리셋, 일괄 내보내기, PNG 투명, 품질 설정, PWA 오프라인 사용이 모두 무료입니다.',
    'about.p3': 'Cloudflare Pages는 정적 자산만 호스팅합니다. 기본 워크플로에는 백엔드가 없습니다.',
    'about.card1Title': '로컬 우선',
    'about.card1Body': '파일은 기기에 남습니다.',
    'about.card2Title': '광고 없음',
    'about.card2Body': '추적 기반 UI가 없습니다.',
    'about.card3Title': '무료 프리셋',
    'about.card3Body': 'Pro 스타일 기능을 개방했습니다.',
    'support.title': '지원',
    'support.uploadTitle': '업로드',
    'support.uploadBody': '업로드 페이지 어디든 사진을 드롭하거나 Ctrl+V로 붙여넣거나 로컬 파일을 선택하세요. 여러 파일을 선택하면 일괄 내보내기가 가능합니다.',
    'support.editTitle': '편집',
    'support.editBody': '프리셋으로 시작한 뒤 테두리 너비, 색상, 비율, 그림자, 모서리, EXIF 텍스트를 조정하세요. 변경 사항은 실시간으로 렌더링됩니다.',
    'support.exportTitle': '내보내기',
    'support.exportBody': 'JPG 또는 PNG, 품질, 크기, 파일 이름을 선택하세요. Ctrl+S는 현재 사진을 내보냅니다.',
    'support.keyboardTitle': '키보드',
    'support.keyboardBody': 'Ctrl+Z 실행 취소, Ctrl+Y 다시 실행, Ctrl+S 내보내기, Esc 업로드 페이지로 돌아가기. macOS에서는 Command도 작동합니다.',
    'support.heicTitle': 'HEIC 참고',
    'support.heicBody': 'HEIC 지원은 브라우저 디코딩과 내장 변환기에 따라 달라집니다. 큰 스마트폰 사진은 로컬 변환 때문에 시간이 더 걸릴 수 있습니다.',
    'notice.appliedPreset': '{name} 적용됨.',
    'notice.savedPreset': '{name}을 로컬 프리셋으로 저장했습니다.',
    'notice.importedPresets': '프리셋 {count}개를 가져왔습니다.',
    'notice.exportReady': '내보내기 준비 완료.',
    'notice.copied': 'PNG를 클립보드에 복사했습니다.',
    'notice.batchExported': '이미지 {count}개를 내보냈습니다.',
    'error.chooseImage': '이미지 파일을 선택하세요.',
    'error.unsupported': '지원하지 않는 형식입니다. JPG, PNG, WebP, HEIC 또는 HEIF를 사용하세요.',
    'error.tooLarge': '파일이 50 MB보다 큽니다. 더 작은 사진을 선택하세요.',
    'error.decode': '브라우저에서 이 이미지를 디코딩할 수 없습니다.',
    'error.loadPhoto': '사진을 불러올 수 없습니다.',
    'error.presetArray': '프리셋 파일은 배열이어야 합니다.',
    'error.importPresets': '프리셋을 가져올 수 없습니다.',
    'error.exportImage': '이 이미지를 내보낼 수 없습니다.',
    'error.copyUnsupported': '이 브라우저는 이미지 클립보드 복사를 지원하지 않습니다.',
    'error.copyImage': '이 이미지를 복사할 수 없습니다.',
    'error.batchExport': '이 이미지들을 일괄 내보낼 수 없습니다.'
  },
  de: {
    'meta.title': 'LumaFrame Foto',
    'meta.description': 'LumaFrame ist ein kostenloser Online-Fotorahmen-Editor mit Rändern, Schatten, EXIF-Beschriftungen, Presets und privatem Browser-Export.',
    'meta.keywords': 'Fotorahmen online, Foto Rand hinzufügen, Foto Rahmen Editor, EXIF Fotorahmen, lokaler Fotoeditor, kostenloser Fotorahmen',
    'nav.support': 'Hilfe',
    'nav.about': 'Über',
    'nav.export': 'Exportieren',
    'nav.language': 'Sprache',
    'nav.light': 'Zum hellen Modus wechseln',
    'nav.dark': 'Zum dunklen Modus wechseln',
    'nav.menu': 'Navigation öffnen',
    'home.dropTitle': 'Foto hier ablegen/einfügen',
    'home.localBlurb': 'JPG, PNG, WebP, HEIC und HEIF werden lokal in diesem Browser verarbeitet. Es wird nichts hochgeladen.',
    'home.select': 'Vom Gerät auswählen',
    'home.localBadge': '100% lokale Verarbeitung',
    'gallery.title': 'Kostenlose Presets enthalten',
    'gallery.subtitle': 'Rahmen, Schatten, Seitenverhältnisse und Metadaten-Vorlagen sind ohne Paywall verfügbar.',
    'gallery.white': 'Weißer Rand',
    'gallery.black': 'Schwarzer Rahmen',
    'gallery.shadow': 'Weicher Schatten',
    'gallery.print': 'Print-Matte',
    'gallery.exif': 'EXIF-Signatur',
    'gallery.cinema': 'Kino 16:9',
    'gallery.square': 'Quadrat Social',
    'gallery.minimalBlack': 'Minimal Schwarz',
    'gallery.rounded': 'Abgerundete Karte',
    'gallery.dark': 'Dunkle Galerie',
    'gallery.large': 'Großer Rand',
    'gallery.clean': 'Klares Weiß',
    'landing.valueTitle': 'Privater Online-Fotorahmen für fertige Bilder',
    'landing.valueBody': 'LumaFrame fügt saubere Ränder, weiche Schatten, runde Ecken, Seitenverhältnis-Leinwände und EXIF-Beschriftungen hinzu, ohne dein Foto hochzuladen. Ideal für den letzten Schritt vor Veröffentlichung, Druck oder Teilen.',
    'landing.privacyTitle': 'Bilder verlassen deinen Browser nicht',
    'landing.privacyBody': 'Die Datei wird lokal dekodiert, Metadaten werden lokal gelesen und JPG oder PNG werden mit Canvas auf deinem Gerät gerendert. Es gibt keinen Upload-Endpunkt im Hauptworkflow.',
    'landing.freeTitle': 'Keine Werbung, kein Wasserzeichen, kein Pro-Gate',
    'landing.freeBody': 'Metadaten-Beschriftungen, eigener Text, gespeicherte Presets, transparente PNGs, Qualitätskontrolle und Stapel-Export sind kostenlos verfügbar.',
    'landing.workflowTitle': 'Drei Schritte',
    'landing.workflow1Title': 'Hochladen',
    'landing.workflow1Body': 'Foto ablegen, einfügen oder lokal auswählen.',
    'landing.workflow2Title': 'Rahmen',
    'landing.workflow2Body': 'Randbreite, Verhältnis, Schatten, Ecken und EXIF-Text anpassen.',
    'landing.workflow3Title': 'Exportieren',
    'landing.workflow3Body': 'JPG oder PNG in Original-, Preset- oder eigener Größe laden.',
    'landing.formatsTitle': 'Unterstützte Formate',
    'landing.formatsBody': 'Unterstützt JPG, PNG, WebP, HEIC und HEIF. HEIC-Konvertierung läuft lokal und kann bei großen Smartphone-Fotos länger dauern.',
    'landing.faqTitle': 'FAQ',
    'landing.faq1Q': 'Ist LumaFrame kostenlos?',
    'landing.faq1A': 'Ja. Der Editor ist kostenlos, werbefrei und fügt keine Wasserzeichen hinzu.',
    'landing.faq2Q': 'Werden meine Fotos hochgeladen?',
    'landing.faq2A': 'Nein. Die Verarbeitung läuft mit Canvas im Browser, und die App lädt deine Bilder nicht hoch.',
    'landing.faq3Q': 'Kann ich Kameradaten anzeigen?',
    'landing.faq3A': 'Ja. LumaFrame kann EXIF-Daten wie Kamera, Objektiv, ISO, Blende, Verschlusszeit, Brennweite, Datum und optional GPS lesen.',
    'landing.faq4Q': 'Kann ich transparente PNG-Dateien exportieren?',
    'landing.faq4A': 'Ja. Wähle PNG und nutze ein transparentes Preset oder reduziere die Randdeckkraft.',
    'editor.noPhotoTitle': 'Kein Foto geladen',
    'editor.noPhotoBody': 'Lade zuerst ein lokales Bild hoch. Die Bearbeitung bleibt vollständig in deinem Browser.',
    'editor.backUpload': 'Zurück zum Upload',
    'editor.newPhoto': 'Neues Foto',
    'editor.addBatch': 'Stapel hinzufügen',
    'preview.label': 'Vorschau von {file}',
    'preset.local': 'Lokales Preset',
    'preset.placeholder': 'Preset-Name',
    'preset.save': 'Aktuelles Preset speichern',
    'preset.export': 'Exportieren',
    'preset.import': 'Importieren',
    'preset.minimal-white.name': 'Minimal Weiß',
    'preset.minimal-white.description': 'Sauberer Galerierand',
    'preset.minimal-black.name': 'Minimal Schwarz',
    'preset.minimal-black.description': 'Kontrastreiche Kante',
    'preset.shadow-white.name': 'Weicher Schatten',
    'preset.shadow-white.description': 'Weißer Rahmen mit Tiefe',
    'preset.photographer-exif.name': 'Fotograf',
    'preset.photographer-exif.description': 'Metadaten-Signatur',
    'preset.instagram-square.name': 'Instagram',
    'preset.instagram-square.description': 'Quadratischer Social-Crop',
    'preset.print-large.name': 'Print',
    'preset.print-large.description': 'Großer unterer Rand',
    'preset.cinema-169.name': 'Kino 16:9',
    'preset.cinema-169.description': 'Breiter matter Rahmen',
    'preset.story-916.name': 'Story',
    'preset.story-916.description': 'Vertikaler Social-Rahmen',
    'preset.rounded-card.name': 'Abgerundet',
    'preset.rounded-card.description': 'Weiche Ecken',
    'preset.transparent-png.name': 'Transparent',
    'preset.transparent-png.description': 'PNG-freundliche Kante',
    'inspector.noPhoto': 'Kein Foto',
    'inspector.uploadBegin': 'Zum Start hochladen',
    'inspector.undo': 'Rückgängig',
    'inspector.redo': 'Wiederholen',
    'inspector.frame': 'Rahmen',
    'inspector.borderUnit': 'Randeinheit',
    'inspector.percent': 'Prozent',
    'inspector.pixels': 'Pixel',
    'inspector.borderWidth': 'Randbreite',
    'inspector.borderColor': 'Randfarbe',
    'inspector.opacity': 'Deckkraft',
    'inspector.aspectRatio': 'Seitenverhältnis',
    'inspector.original': 'Original',
    'inspector.custom': 'Benutzerdefiniert',
    'inspector.width': 'Breite',
    'inspector.height': 'Höhe',
    'inspector.shadow': 'Schatten',
    'inspector.enableShadow': 'Schatten aktivieren',
    'inspector.none': 'Kein',
    'inspector.light': 'Leicht',
    'inspector.medium': 'Mittel',
    'inspector.strong': 'Stark',
    'inspector.strength': 'Stärke',
    'inspector.blur': 'Unschärfe',
    'inspector.offsetX': 'Versatz X',
    'inspector.offsetY': 'Versatz Y',
    'inspector.shadowColor': 'Schattenfarbe',
    'inspector.corners': 'Ecken',
    'inspector.roundedCorners': 'Runde Ecken',
    'inspector.linkCorners': 'Alle Ecken koppeln',
    'inspector.radius': 'Radius',
    'inspector.topL': 'Oben L',
    'inspector.topR': 'Oben R',
    'inspector.bottomR': 'Unten R',
    'inspector.bottomL': 'Unten L',
    'inspector.metadata': 'Metadaten',
    'inspector.showExif': 'EXIF und Text anzeigen',
    'inspector.position': 'Position',
    'inspector.customText': 'Eigener Text',
    'inspector.customTextPlaceholder': '@deinname oder Signatur',
    'inspector.fontSize': 'Schriftgröße',
    'inspector.textColor': 'Textfarbe',
    'inspector.camera': 'Kamera',
    'inspector.lens': 'Objektiv',
    'inspector.exposure': 'Belichtung',
    'inspector.date': 'Datum',
    'inspector.gps': 'GPS',
    'inspector.exportSection': 'Export',
    'inspector.format': 'Format',
    'inspector.quality': 'Qualität',
    'inspector.size': 'Größe',
    'inspector.preset': 'Preset',
    'inspector.longEdge': 'Lange Kante',
    'inspector.fileName': 'Dateiname',
    'inspector.filePlaceholder': 'original_framed',
    'inspector.download': 'Download',
    'inspector.copy': 'Kopieren',
    'inspector.batchExport': 'Stapel-Export',
    'about.title': 'Private Fotorahmen, ohne Bezahlschranke.',
    'about.p1': 'LumaFrame ist ein reines Frontend-Tool. Dekodierung, EXIF-Analyse, Vorschau und Export laufen mit Canvas im Browser. Originalbilder, Ergebnisse, Presets und Metadaten werden nicht hochgeladen.',
    'about.p2': 'Alle Funktionen sind kostenlos: EXIF-Text, Signaturen, Rahmen, Schatten, runde Ecken, Presets, Stapel-Export, PNG-Transparenz, Qualitätseinstellungen und PWA-Offline-Nutzung.',
    'about.p3': 'Cloudflare Pages hostet nur statische Dateien. Im Hauptworkflow gibt es kein Backend.',
    'about.card1Title': 'Lokal zuerst',
    'about.card1Body': 'Dateien bleiben auf dem Gerät.',
    'about.card2Title': 'Keine Werbung',
    'about.card2Body': 'Keine Tracking-getriebene Oberfläche.',
    'about.card3Title': 'Kostenlose Presets',
    'about.card3Body': 'Pro-ähnliche Funktionen sind offen.',
    'support.title': 'Hilfe',
    'support.uploadTitle': 'Upload',
    'support.uploadBody': 'Lege ein Foto auf der Upload-Seite ab, füge es mit Ctrl+V ein oder wähle eine lokale Datei. Mehrere Dateien aktivieren den Stapel-Export.',
    'support.editTitle': 'Bearbeiten',
    'support.editBody': 'Starte mit einem Preset und passe Rand, Farbe, Seitenverhältnis, Schatten, Ecken und EXIF-Text an. Änderungen werden live gerendert.',
    'support.exportTitle': 'Export',
    'support.exportBody': 'Wähle JPG oder PNG, Qualität, Größe und Dateiname. Ctrl+S exportiert das aktive Foto.',
    'support.keyboardTitle': 'Tastatur',
    'support.keyboardBody': 'Ctrl+Z rückgängig, Ctrl+Y wiederholen, Ctrl+S exportieren, Esc zurück zum Upload. Auf macOS funktioniert auch Command.',
    'support.heicTitle': 'HEIC-Hinweise',
    'support.heicBody': 'HEIC hängt von Browser-Dekodierung und dem integrierten Konverter ab. Sehr große Smartphone-Fotos können länger dauern.',
    'notice.appliedPreset': '{name} angewendet.',
    'notice.savedPreset': '{name} als lokales Preset gespeichert.',
    'notice.importedPresets': '{count} Preset(s) importiert.',
    'notice.exportReady': 'Export bereit.',
    'notice.copied': 'PNG in die Zwischenablage kopiert.',
    'notice.batchExported': '{count} Bild(er) exportiert.',
    'error.chooseImage': 'Bitte wähle eine Bilddatei.',
    'error.unsupported': 'Nicht unterstütztes Format. Bitte JPG, PNG, WebP, HEIC oder HEIF verwenden.',
    'error.tooLarge': 'Diese Datei ist größer als 50 MB. Bitte wähle ein kleineres Foto.',
    'error.decode': 'Der Browser konnte dieses Bild nicht dekodieren.',
    'error.loadPhoto': 'Dieses Foto konnte nicht geladen werden.',
    'error.presetArray': 'Die Preset-Datei muss ein Array sein.',
    'error.importPresets': 'Presets konnten nicht importiert werden.',
    'error.exportImage': 'Dieses Bild konnte nicht exportiert werden.',
    'error.copyUnsupported': 'Dieser Browser unterstützt das Kopieren von Bildern nicht.',
    'error.copyImage': 'Dieses Bild konnte nicht kopiert werden.',
    'error.batchExport': 'Diese Bilder konnten nicht im Stapel exportiert werden.'
  },
  fr: {
    'meta.title': 'LumaFrame Photo',
    'meta.description': 'LumaFrame est un éditeur gratuit de cadres photo en ligne avec bordures, ombres, légendes EXIF, préréglages et export privé dans le navigateur.',
    'meta.keywords': 'cadre photo en ligne, ajouter bordure photo, éditeur cadre photo, cadre EXIF, éditeur photo local, cadre photo gratuit',
    'nav.support': 'Aide',
    'nav.about': 'À propos',
    'nav.export': 'Exporter',
    'nav.language': 'Langue',
    'nav.light': 'Passer au mode clair',
    'nav.dark': 'Passer au mode sombre',
    'nav.menu': 'Ouvrir la navigation',
    'home.dropTitle': 'Déposez/collez une photo ici',
    'home.localBlurb': 'JPG, PNG, WebP, HEIC et HEIF sont traités localement dans ce navigateur. Rien n’est téléversé.',
    'home.select': 'Choisir depuis votre appareil',
    'home.localBadge': 'Traitement 100% local',
    'gallery.title': 'Préréglages gratuits inclus',
    'gallery.subtitle': 'Bordures, ombres, ratios et modèles de métadonnées sont disponibles sans paywall.',
    'gallery.white': 'Bord blanc',
    'gallery.black': 'Cadre noir',
    'gallery.shadow': 'Ombre douce',
    'gallery.print': 'Marge tirage',
    'gallery.exif': 'Signature EXIF',
    'gallery.cinema': 'Cinéma 16:9',
    'gallery.square': 'Carré social',
    'gallery.minimalBlack': 'Noir minimal',
    'gallery.rounded': 'Carte arrondie',
    'gallery.dark': 'Galerie sombre',
    'gallery.large': 'Large bordure',
    'gallery.clean': 'Blanc net',
    'landing.valueTitle': 'Un éditeur de cadres photo privé pour images finalisées',
    'landing.valueBody': 'LumaFrame ajoute bordures nettes, ombres douces, coins arrondis, canevas de ratio et légendes EXIF sans envoyer votre photo. Idéal avant publication, impression ou partage.',
    'landing.privacyTitle': 'Les images ne quittent jamais votre navigateur',
    'landing.privacyBody': 'Le fichier est décodé localement, les métadonnées sont lues localement et le JPG ou PNG final est rendu avec Canvas sur votre appareil. Le flux principal n’a pas de point d’envoi.',
    'landing.freeTitle': 'Sans publicité, sans filigrane, sans option Pro',
    'landing.freeBody': 'Légendes de métadonnées, texte personnalisé, préréglages, PNG transparent, qualité et export par lot sont gratuits.',
    'landing.workflowTitle': 'Flux en trois étapes',
    'landing.workflow1Title': 'Importer',
    'landing.workflow1Body': 'Déposez, collez ou choisissez une photo locale.',
    'landing.workflow2Title': 'Encadrer',
    'landing.workflow2Body': 'Ajustez bordure, ratio, ombre, coins et texte EXIF.',
    'landing.workflow3Title': 'Exporter',
    'landing.workflow3Body': 'Téléchargez JPG ou PNG en taille originale, préréglée ou personnalisée.',
    'landing.formatsTitle': 'Formats pris en charge',
    'landing.formatsBody': 'Compatible JPG, PNG, WebP, HEIC et HEIF. La conversion HEIC se fait localement et peut être plus longue pour de très grandes photos.',
    'landing.faqTitle': 'FAQ',
    'landing.faq1Q': 'LumaFrame est-il gratuit ?',
    'landing.faq1A': 'Oui. L’éditeur est gratuit, sans publicité et sans filigrane.',
    'landing.faq2Q': 'Mes photos sont-elles envoyées ?',
    'landing.faq2A': 'Non. Le traitement s’exécute dans votre navigateur avec Canvas, et l’application n’envoie pas vos images.',
    'landing.faq3Q': 'Puis-je afficher les métadonnées de l’appareil ?',
    'landing.faq3A': 'Oui. LumaFrame peut lire appareil, objectif, ISO, ouverture, vitesse, focale, date et GPS optionnel.',
    'landing.faq4Q': 'Puis-je exporter des PNG transparents ?',
    'landing.faq4A': 'Oui. Choisissez PNG et utilisez un préréglage transparent ou baissez l’opacité de la bordure.',
    'editor.noPhotoTitle': 'Aucune photo chargée',
    'editor.noPhotoBody': 'Importez d’abord une image locale. L’éditeur garde tout le traitement dans votre navigateur.',
    'editor.backUpload': 'Retour à l’import',
    'editor.newPhoto': 'Nouvelle photo',
    'editor.addBatch': 'Ajouter un lot',
    'preview.label': 'Aperçu de {file}',
    'preset.local': 'Préréglage local',
    'preset.placeholder': 'Nom du préréglage',
    'preset.save': 'Enregistrer le préréglage actuel',
    'preset.export': 'Exporter',
    'preset.import': 'Importer',
    'preset.minimal-white.name': 'Blanc minimal',
    'preset.minimal-white.description': 'Bordure galerie nette',
    'preset.minimal-black.name': 'Noir minimal',
    'preset.minimal-black.description': 'Bord très contrasté',
    'preset.shadow-white.name': 'Ombre douce',
    'preset.shadow-white.description': 'Cadre blanc avec profondeur',
    'preset.photographer-exif.name': 'Photographe',
    'preset.photographer-exif.description': 'Signature métadonnées',
    'preset.instagram-square.name': 'Instagram',
    'preset.instagram-square.description': 'Recadrage carré social',
    'preset.print-large.name': 'Tirage',
    'preset.print-large.description': 'Grande marge inférieure',
    'preset.cinema-169.name': 'Cinéma 16:9',
    'preset.cinema-169.description': 'Cadre mat large',
    'preset.story-916.name': 'Story',
    'preset.story-916.description': 'Cadre social vertical',
    'preset.rounded-card.name': 'Arrondi',
    'preset.rounded-card.description': 'Coins doux',
    'preset.transparent-png.name': 'Transparent',
    'preset.transparent-png.description': 'Bord adapté au PNG',
    'inspector.noPhoto': 'Aucune photo',
    'inspector.uploadBegin': 'Importer pour commencer',
    'inspector.undo': 'Annuler',
    'inspector.redo': 'Rétablir',
    'inspector.frame': 'Cadre',
    'inspector.borderUnit': 'Unité de bordure',
    'inspector.percent': 'Pourcentage',
    'inspector.pixels': 'Pixels',
    'inspector.borderWidth': 'Largeur de bordure',
    'inspector.borderColor': 'Couleur de bordure',
    'inspector.opacity': 'Opacité',
    'inspector.aspectRatio': 'Ratio',
    'inspector.original': 'Original',
    'inspector.custom': 'Personnalisé',
    'inspector.width': 'Largeur',
    'inspector.height': 'Hauteur',
    'inspector.shadow': 'Ombre',
    'inspector.enableShadow': 'Activer l’ombre',
    'inspector.none': 'Aucune',
    'inspector.light': 'Légère',
    'inspector.medium': 'Moyenne',
    'inspector.strong': 'Forte',
    'inspector.strength': 'Intensité',
    'inspector.blur': 'Flou',
    'inspector.offsetX': 'Décalage X',
    'inspector.offsetY': 'Décalage Y',
    'inspector.shadowColor': 'Couleur de l’ombre',
    'inspector.corners': 'Coins',
    'inspector.roundedCorners': 'Coins arrondis',
    'inspector.linkCorners': 'Lier les coins',
    'inspector.radius': 'Rayon',
    'inspector.topL': 'Haut G',
    'inspector.topR': 'Haut D',
    'inspector.bottomR': 'Bas D',
    'inspector.bottomL': 'Bas G',
    'inspector.metadata': 'Métadonnées',
    'inspector.showExif': 'Afficher EXIF et texte',
    'inspector.position': 'Position',
    'inspector.customText': 'Texte personnalisé',
    'inspector.customTextPlaceholder': '@votrenom ou signature',
    'inspector.fontSize': 'Taille de police',
    'inspector.textColor': 'Couleur du texte',
    'inspector.camera': 'Appareil',
    'inspector.lens': 'Objectif',
    'inspector.exposure': 'Exposition',
    'inspector.date': 'Date',
    'inspector.gps': 'GPS',
    'inspector.exportSection': 'Export',
    'inspector.format': 'Format',
    'inspector.quality': 'Qualité',
    'inspector.size': 'Taille',
    'inspector.preset': 'Préréglage',
    'inspector.longEdge': 'Grand côté',
    'inspector.fileName': 'Nom de fichier',
    'inspector.filePlaceholder': 'original_framed',
    'inspector.download': 'Télécharger',
    'inspector.copy': 'Copier',
    'inspector.batchExport': 'Export par lot',
    'about.title': 'Encadrer vos photos en privé, sans barrière.',
    'about.p1': 'LumaFrame est un outil purement frontend. Décodage photo, lecture EXIF, aperçu et export final se font dans le navigateur avec Canvas. L’application n’envoie pas les images, rendus, préréglages ou métadonnées à un serveur.',
    'about.p2': 'Toutes les fonctions sont gratuites: texte EXIF, signatures, bordures, ombres, coins arrondis, préréglages, export par lot, PNG transparent, qualité et PWA hors ligne.',
    'about.p3': 'Cloudflare Pages héberge uniquement les fichiers statiques. Le flux principal n’a pas de backend.',
    'about.card1Title': 'Local d’abord',
    'about.card1Body': 'Les fichiers restent sur l’appareil.',
    'about.card2Title': 'Sans publicité',
    'about.card2Body': 'Pas d’interface basée sur le suivi.',
    'about.card3Title': 'Préréglages gratuits',
    'about.card3Body': 'Les contrôles de type Pro sont ouverts.',
    'support.title': 'Aide',
    'support.uploadTitle': 'Import',
    'support.uploadBody': 'Déposez une photo sur la page d’import, collez avec Ctrl+V ou choisissez un fichier local. Plusieurs fichiers activent l’export par lot.',
    'support.editTitle': 'Édition',
    'support.editBody': 'Partez d’un préréglage, puis ajustez bordure, couleur, ratio, ombre, coins et texte EXIF. Les changements sont rendus en direct.',
    'support.exportTitle': 'Export',
    'support.exportBody': 'Choisissez JPG ou PNG, qualité, taille et nom de fichier. Ctrl+S exporte la photo active.',
    'support.keyboardTitle': 'Clavier',
    'support.keyboardBody': 'Ctrl+Z annuler, Ctrl+Y rétablir, Ctrl+S exporter, Esc revenir à l’import. Sur macOS, Command fonctionne aussi.',
    'support.heicTitle': 'Notes HEIC',
    'support.heicBody': 'La prise en charge HEIC dépend du navigateur et du convertisseur inclus. Les très grandes photos peuvent prendre plus de temps.',
    'notice.appliedPreset': '{name} appliqué.',
    'notice.savedPreset': '{name} enregistré comme préréglage local.',
    'notice.importedPresets': '{count} préréglage(s) importé(s).',
    'notice.exportReady': 'Export prêt.',
    'notice.copied': 'PNG copié dans le presse-papiers.',
    'notice.batchExported': '{count} image(s) exportée(s).',
    'error.chooseImage': 'Choisissez un fichier image.',
    'error.unsupported': 'Format non pris en charge. Utilisez JPG, PNG, WebP, HEIC ou HEIF.',
    'error.tooLarge': 'Ce fichier dépasse 50 Mo. Choisissez une photo plus petite.',
    'error.decode': 'Le navigateur n’a pas pu décoder cette image.',
    'error.loadPhoto': 'Impossible de charger cette photo.',
    'error.presetArray': 'Le fichier de préréglages doit être un tableau.',
    'error.importPresets': 'Impossible d’importer les préréglages.',
    'error.exportImage': 'Impossible d’exporter cette image.',
    'error.copyUnsupported': 'Ce navigateur ne prend pas en charge la copie d’image.',
    'error.copyImage': 'Impossible de copier cette image.',
    'error.batchExport': 'Impossible d’exporter ces images par lot.'
  },
  pt: {
    'meta.title': 'LumaFrame Foto',
    'meta.description': 'LumaFrame é um editor gratuito de molduras para fotos online com bordas, sombras, legendas EXIF, predefinições e exportação privada no navegador.',
    'meta.keywords': 'moldura para foto online, adicionar borda à foto, editor de moldura, moldura EXIF, editor local de fotos, moldura grátis',
    'nav.support': 'Suporte',
    'nav.about': 'Sobre',
    'nav.export': 'Exportar',
    'nav.language': 'Idioma',
    'nav.light': 'Mudar para modo claro',
    'nav.dark': 'Mudar para modo escuro',
    'nav.menu': 'Abrir navegação',
    'home.dropTitle': 'Solte/cole uma foto aqui',
    'home.localBlurb': 'JPG, PNG, WebP, HEIC e HEIF são processados localmente neste navegador. Nada é enviado.',
    'home.select': 'Selecionar do dispositivo',
    'home.localBadge': 'Processamento 100% local',
    'gallery.title': 'Predefinições grátis incluídas',
    'gallery.subtitle': 'Bordas, sombras, proporções e modelos de metadados disponíveis sem paywall.',
    'gallery.white': 'Borda branca',
    'gallery.black': 'Moldura preta',
    'gallery.shadow': 'Sombra suave',
    'gallery.print': 'Margem de impressão',
    'gallery.exif': 'Assinatura EXIF',
    'gallery.cinema': 'Cinema 16:9',
    'gallery.square': 'Quadrado social',
    'gallery.minimalBlack': 'Preto minimalista',
    'gallery.rounded': 'Cartão arredondado',
    'gallery.dark': 'Galeria escura',
    'gallery.large': 'Borda grande',
    'gallery.clean': 'Branco limpo',
    'landing.valueTitle': 'Editor privado de molduras online para fotos finalizadas',
    'landing.valueBody': 'LumaFrame adiciona bordas limpas, sombras suaves, cantos arredondados, telas proporcionais e legendas EXIF sem enviar sua foto. Feito para o acabamento antes de postar, imprimir ou compartilhar.',
    'landing.privacyTitle': 'As imagens nunca saem do navegador',
    'landing.privacyBody': 'O arquivo é decodificado localmente, os metadados são lidos localmente e o JPG ou PNG final é renderizado com Canvas no seu dispositivo. Não há endpoint de upload no fluxo principal.',
    'landing.freeTitle': 'Sem anúncios, sem marca d’água, sem bloqueio Pro',
    'landing.freeBody': 'Legendas de metadados, texto personalizado, predefinições salvas, PNG transparente, controle de qualidade e exportação em lote são gratuitos.',
    'landing.workflowTitle': 'Fluxo em três etapas',
    'landing.workflow1Title': 'Enviar',
    'landing.workflow1Body': 'Solte, cole ou escolha uma foto local.',
    'landing.workflow2Title': 'Emoldurar',
    'landing.workflow2Body': 'Ajuste borda, proporção, sombra, cantos e texto EXIF.',
    'landing.workflow3Title': 'Exportar',
    'landing.workflow3Body': 'Baixe JPG ou PNG em tamanho original, predefinido ou personalizado.',
    'landing.formatsTitle': 'Formatos suportados',
    'landing.formatsBody': 'Funciona com JPG, PNG, WebP, HEIC e HEIF. A conversão HEIC acontece localmente e pode demorar mais em fotos grandes de celular.',
    'landing.faqTitle': 'FAQ',
    'landing.faq1Q': 'LumaFrame é gratuito?',
    'landing.faq1A': 'Sim. O editor é gratuito, sem anúncios e sem marca d’água.',
    'landing.faq2Q': 'Minhas fotos são enviadas?',
    'landing.faq2A': 'Não. O processamento roda no navegador com Canvas, e o app não envia suas imagens.',
    'landing.faq3Q': 'Posso mostrar metadados da câmera?',
    'landing.faq3A': 'Sim. LumaFrame pode ler câmera, lente, ISO, abertura, velocidade, distância focal, data e GPS opcional.',
    'landing.faq4Q': 'Posso exportar PNG transparente?',
    'landing.faq4A': 'Sim. Escolha PNG e use uma predefinição transparente ou reduza a opacidade da borda.',
    'editor.noPhotoTitle': 'Nenhuma foto carregada',
    'editor.noPhotoBody': 'Envie primeiro uma imagem local. O editor mantém todo o processamento no navegador.',
    'editor.backUpload': 'Voltar ao envio',
    'editor.newPhoto': 'Nova foto',
    'editor.addBatch': 'Adicionar lote',
    'preview.label': 'Prévia de {file}',
    'preset.local': 'Predefinição local',
    'preset.placeholder': 'Nome da predefinição',
    'preset.save': 'Salvar predefinição atual',
    'preset.export': 'Exportar',
    'preset.import': 'Importar',
    'preset.minimal-white.name': 'Branco minimalista',
    'preset.minimal-white.description': 'Borda limpa de galeria',
    'preset.minimal-black.name': 'Preto minimalista',
    'preset.minimal-black.description': 'Borda de alto contraste',
    'preset.shadow-white.name': 'Sombra suave',
    'preset.shadow-white.description': 'Moldura branca com profundidade',
    'preset.photographer-exif.name': 'Fotógrafo',
    'preset.photographer-exif.description': 'Assinatura de metadados',
    'preset.instagram-square.name': 'Instagram',
    'preset.instagram-square.description': 'Corte social quadrado',
    'preset.print-large.name': 'Impressão',
    'preset.print-large.description': 'Margem inferior ampla',
    'preset.cinema-169.name': 'Cinema 16:9',
    'preset.cinema-169.description': 'Moldura fosca ampla',
    'preset.story-916.name': 'Story',
    'preset.story-916.description': 'Moldura social vertical',
    'preset.rounded-card.name': 'Arredondado',
    'preset.rounded-card.description': 'Cantos suaves',
    'preset.transparent-png.name': 'Transparente',
    'preset.transparent-png.description': 'Borda ideal para PNG',
    'inspector.noPhoto': 'Sem foto',
    'inspector.uploadBegin': 'Envie para começar',
    'inspector.undo': 'Desfazer',
    'inspector.redo': 'Refazer',
    'inspector.frame': 'Moldura',
    'inspector.borderUnit': 'Unidade da borda',
    'inspector.percent': 'Percentual',
    'inspector.pixels': 'Pixels',
    'inspector.borderWidth': 'Largura da borda',
    'inspector.borderColor': 'Cor da borda',
    'inspector.opacity': 'Opacidade',
    'inspector.aspectRatio': 'Proporção',
    'inspector.original': 'Original',
    'inspector.custom': 'Personalizado',
    'inspector.width': 'Largura',
    'inspector.height': 'Altura',
    'inspector.shadow': 'Sombra',
    'inspector.enableShadow': 'Ativar sombra',
    'inspector.none': 'Nenhuma',
    'inspector.light': 'Leve',
    'inspector.medium': 'Média',
    'inspector.strong': 'Forte',
    'inspector.strength': 'Intensidade',
    'inspector.blur': 'Desfoque',
    'inspector.offsetX': 'Desloc. X',
    'inspector.offsetY': 'Desloc. Y',
    'inspector.shadowColor': 'Cor da sombra',
    'inspector.corners': 'Cantos',
    'inspector.roundedCorners': 'Cantos arredondados',
    'inspector.linkCorners': 'Vincular cantos',
    'inspector.radius': 'Raio',
    'inspector.topL': 'Sup. E',
    'inspector.topR': 'Sup. D',
    'inspector.bottomR': 'Inf. D',
    'inspector.bottomL': 'Inf. E',
    'inspector.metadata': 'Metadados',
    'inspector.showExif': 'Mostrar EXIF e texto',
    'inspector.position': 'Posição',
    'inspector.customText': 'Texto personalizado',
    'inspector.customTextPlaceholder': '@seunome ou assinatura',
    'inspector.fontSize': 'Tamanho da fonte',
    'inspector.textColor': 'Cor do texto',
    'inspector.camera': 'Câmera',
    'inspector.lens': 'Lente',
    'inspector.exposure': 'Exposição',
    'inspector.date': 'Data',
    'inspector.gps': 'GPS',
    'inspector.exportSection': 'Exportar',
    'inspector.format': 'Formato',
    'inspector.quality': 'Qualidade',
    'inspector.size': 'Tamanho',
    'inspector.preset': 'Predefinição',
    'inspector.longEdge': 'Lado maior',
    'inspector.fileName': 'Nome do arquivo',
    'inspector.filePlaceholder': 'original_framed',
    'inspector.download': 'Baixar',
    'inspector.copy': 'Copiar',
    'inspector.batchExport': 'Exportar lote',
    'about.title': 'Molduras privadas para fotos, sem cobrança.',
    'about.p1': 'LumaFrame é uma ferramenta 100% frontend. Decodificação, leitura EXIF, prévia e exportação final acontecem no navegador com Canvas. O app não envia imagens, resultados, predefinições ou metadados a um servidor.',
    'about.p2': 'Todos os recursos são gratuitos: texto EXIF, assinaturas, bordas, sombras, cantos arredondados, predefinições, exportação em lote, PNG transparente, qualidade e PWA offline.',
    'about.p3': 'Cloudflare Pages hospeda apenas arquivos estáticos. O fluxo principal não tem backend.',
    'about.card1Title': 'Local primeiro',
    'about.card1Body': 'Os arquivos ficam no dispositivo.',
    'about.card2Title': 'Sem anúncios',
    'about.card2Body': 'Sem interface movida por rastreamento.',
    'about.card3Title': 'Predefinições grátis',
    'about.card3Body': 'Controles estilo Pro estão abertos.',
    'support.title': 'Suporte',
    'support.uploadTitle': 'Envio',
    'support.uploadBody': 'Solte uma foto na página de envio, cole com Ctrl+V ou escolha um arquivo local. Vários arquivos ativam a exportação em lote.',
    'support.editTitle': 'Editar',
    'support.editBody': 'Comece por uma predefinição e ajuste borda, cor, proporção, sombra, cantos e texto EXIF. As mudanças aparecem em tempo real.',
    'support.exportTitle': 'Exportar',
    'support.exportBody': 'Escolha JPG ou PNG, qualidade, tamanho e nome do arquivo. Ctrl+S exporta a foto ativa.',
    'support.keyboardTitle': 'Teclado',
    'support.keyboardBody': 'Ctrl+Z desfaz, Ctrl+Y refaz, Ctrl+S exporta, Esc volta ao envio. No macOS, Command também funciona.',
    'support.heicTitle': 'Notas sobre HEIC',
    'support.heicBody': 'O suporte a HEIC depende da decodificação do navegador e do conversor incluído. Fotos grandes de celular podem demorar mais.',
    'notice.appliedPreset': '{name} aplicado.',
    'notice.savedPreset': '{name} salvo como predefinição local.',
    'notice.importedPresets': '{count} predefinição(ões) importada(s).',
    'notice.exportReady': 'Exportação pronta.',
    'notice.copied': 'PNG copiado para a área de transferência.',
    'notice.batchExported': '{count} imagem(ns) exportada(s).',
    'error.chooseImage': 'Escolha um arquivo de imagem.',
    'error.unsupported': 'Formato não suportado. Use JPG, PNG, WebP, HEIC ou HEIF.',
    'error.tooLarge': 'Este arquivo tem mais de 50 MB. Escolha uma foto menor.',
    'error.decode': 'O navegador não conseguiu decodificar esta imagem.',
    'error.loadPhoto': 'Não foi possível carregar esta foto.',
    'error.presetArray': 'O arquivo de predefinições deve ser um array.',
    'error.importPresets': 'Não foi possível importar predefinições.',
    'error.exportImage': 'Não foi possível exportar esta imagem.',
    'error.copyUnsupported': 'Este navegador não suporta copiar imagem para a área de transferência.',
    'error.copyImage': 'Não foi possível copiar esta imagem.',
    'error.batchExport': 'Não foi possível exportar estas imagens em lote.'
  }
};

interface LanguageContextValue {
  locale: Locale;
  localeOptions: typeof localeOptions;
  setLocale: (locale: string) => void;
  t: (key: string, params?: Record<string, string | number>) => string;
}

const LanguageContext = createContext<LanguageContextValue | null>(null);

function initialLocale(): Locale {
  const fromUrl = new URLSearchParams(window.location.search).get('lang');
  if (fromUrl && localeOptions.some((option) => option.value === fromUrl)) return fromUrl as Locale;

  const saved = localStorage.getItem(LOCALE_KEY);
  if (saved && localeOptions.some((option) => option.value === saved)) return saved as Locale;

  const language = navigator.language.toLowerCase();
  if (language.startsWith('zh')) return 'zh';
  if (language.startsWith('ja')) return 'ja';
  if (language.startsWith('ko')) return 'ko';
  if (language.startsWith('de')) return 'de';
  if (language.startsWith('fr')) return 'fr';
  if (language.startsWith('pt')) return 'pt';
  return DEFAULT_LOCALE as Locale;
}

export function LanguageProvider({ children }: { children: ReactNode }): JSX.Element {
  const [locale, setLocaleState] = useState<Locale>(initialLocale);

  const setLocale = useCallback((next: string) => {
    if (localeOptions.some((option) => option.value === next)) {
      setLocaleState(next as Locale);
      const url = new URL(window.location.href);
      url.searchParams.set('lang', next);
      window.history.replaceState({}, '', `${url.pathname}${url.search}${url.hash}`);
    }
  }, []);

  const t = useCallback(
    (key: string, params: Record<string, string | number> = {}) => {
      const raw = dictionaries[locale][key] ?? dictionaries.en[key] ?? key;
      return Object.entries(params).reduce((value, [name, replacement]) => value.split(`{${name}}`).join(String(replacement)), raw);
    },
    [locale]
  );

  useEffect(() => {
    localStorage.setItem(LOCALE_KEY, locale);
    document.documentElement.lang = localeOptions.find((option) => option.value === locale)?.htmlLang ?? 'en';
    document.title = t('meta.title');
    document.querySelector('meta[name="description"]')?.setAttribute('content', t('meta.description'));
  }, [locale, t]);

  const value = useMemo(() => ({ locale, localeOptions, setLocale, t }), [locale, setLocale, t]);

  return <LanguageContext.Provider value={value}>{children}</LanguageContext.Provider>;
}

export function useLanguage(): LanguageContextValue {
  const context = useContext(LanguageContext);
  if (!context) throw new Error('useLanguage must be used inside LanguageProvider.');
  return context;
}
